import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, CheckCircle2, XCircle, Eye, EyeOff, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, saveAuth } from "../lib/api";

// Matches MIN_PASSWORD_LENGTH on the server and the Register page rule.
const MIN_PASSWORD_LENGTH = 4;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [status, setStatus] = useState("checking"); // checking | ready | invalid | done
  const [errorMsg, setErrorMsg] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      setErrorMsg("This reset link is missing its token.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/reset-password/validate", { params: { token } });
        setEmail(data.email || "");
        setStatus("ready");
      } catch (err) {
        setStatus("invalid");
        setErrorMsg(err?.response?.data?.detail || "This reset link is invalid or has expired.");
      }
    })();
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      toast.error(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (password !== confirm) {
      toast.error("The two passwords don't match");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      saveAuth(data.token, data.user);
      setStatus("done");
      toast.success(`Password updated, ${data.user.name}!`);
      setTimeout(() => navigate(data.user.grade_level ? "/dashboard" : "/profile"), 1200);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not reset your password.";
      if (typeof detail === "string" && detail.toLowerCase().includes("link")) {
        setStatus("invalid");
        setErrorMsg(detail);
      } else {
        toast.error(detail);
      }
    } finally { setSaving(false); }
  };

  const inputClass =
    "w-full rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-5 py-4 text-lg outline-none transition-all font-semibold";

  return (
    <div className="min-h-screen bg-[#F4F9FF] bg-polka flex items-center justify-center p-5">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", bounce: 0.5, duration: 0.6 }}
        className="w-full max-w-md"
      >
        {status === "checking" && (
          <div className="card-soft text-center space-y-4" data-testid="reset-password-checking">
            <div className="inline-flex w-20 h-20 rounded-3xl bg-[#FBBF24] items-center justify-center shadow-[0_8px_0_#B45309] mb-2 animate-pulse">
              <Sparkles className="w-10 h-10 text-[#1E1B4B]" strokeWidth={3} />
            </div>
            <h1 className="font-display text-3xl font-bold text-[#1E1B4B]">Checking your link…</h1>
          </div>
        )}

        {status === "ready" && (
          <>
            <div className="text-center mb-6">
              <div className="inline-flex w-20 h-20 rounded-3xl bg-[#4D96FF] items-center justify-center shadow-[0_8px_0_#1D4ED8] mb-4">
                <Lock className="w-10 h-10 text-white" strokeWidth={3} />
              </div>
              <h1 className="font-display text-4xl font-bold text-[#1E1B4B]">New password</h1>
              {email && (
                <p className="text-lg text-slate-600 mt-1 font-semibold" data-testid="reset-password-email">
                  for {email}
                </p>
              )}
            </div>

            <form onSubmit={onSubmit} className="card-soft space-y-4" data-testid="reset-password-form">
              <div>
                <label className="block text-sm font-bold text-[#1E1B4B] mb-1.5 ml-2">New password</label>
                <div className="relative">
                  <input
                    type={show ? "text" : "password"} required value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`${inputClass} pr-14`}
                    placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button" onClick={() => setShow((s) => !s)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#4D96FF]"
                    aria-label={show ? "Hide password" : "Show password"}
                    data-testid="reset-password-toggle-visibility"
                  >
                    {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-[#1E1B4B] mb-1.5 ml-2">Type it again</label>
                <input
                  type={show ? "text" : "password"} required value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className={inputClass}
                  placeholder="Same password"
                  data-testid="reset-password-confirm-input"
                />
              </div>
              <button type="submit" disabled={saving} className="btn-clay w-full" data-testid="reset-password-submit-btn">
                <Lock className="w-5 h-5" strokeWidth={3} /> {saving ? "Saving…" : "Save new password"}
              </button>
            </form>
          </>
        )}

        {status === "done" && (
          <div className="card-soft text-center space-y-4" data-testid="reset-password-done">
            <div className="inline-flex w-20 h-20 rounded-3xl bg-[#4ADE80] items-center justify-center shadow-[0_8px_0_#15803D] mb-2">
              <CheckCircle2 className="w-10 h-10 text-white" strokeWidth={3} />
            </div>
            <h1 className="font-display text-3xl font-bold text-[#1E1B4B]">Password updated!</h1>
            <p className="text-slate-600 font-semibold">Taking you to your dashboard…</p>
          </div>
        )}

        {status === "invalid" && (
          <div className="card-soft text-center space-y-4" data-testid="reset-password-invalid">
            <div className="inline-flex w-20 h-20 rounded-3xl bg-[#FF6B6B] items-center justify-center shadow-[0_8px_0_#9F1239] mb-2">
              <XCircle className="w-10 h-10 text-white" strokeWidth={3} />
            </div>
            <h1 className="font-display text-2xl font-bold text-[#1E1B4B]">Link didn't work</h1>
            <p className="text-slate-600 font-semibold">{errorMsg}</p>
            <Link to="/forgot-password" className="btn-clay w-full inline-flex" data-testid="reset-password-request-new-link">
              Get a new link
            </Link>
            <Link to="/login" className="block text-sm font-bold text-[#4D96FF] hover:underline" data-testid="reset-password-login-link">
              Back to login
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
